import React, { FC } from 'react';
import { useIntl } from 'react-intl';
import { useLocation } from 'react-router-dom';
import { makeStyles } from '@material-ui/core';
import some from 'lodash/some';
import useRootSelector from 'common/hooks/useRootSelector';
import { selectCurrentUserCorporation } from 'modules/authentication/state/selectors';
import { PoliciesPermissionSetItems as Permission } from 'common/constants/permissions';
import { RouteContainerChild, RouteItem } from 'types/route.types';
import { IRouteItemContainer } from 'interfaces/route.interfaces';
import sidebarMessages from 'common/messages/sidebar';
import useHasPermissions from 'common/hooks/useHasPermissions';
import useSidebarContext from 'common/hooks/context/useSidebarContext';
import SidebarContainer from 'components/Sidebar/SidebarContainer';
import SideBarNavLink from 'components/Sidebar/SidebarNavLink';
import SidebarGroupTitle from 'components/Sidebar/SidebarGroupTitle';
import SidebarDivider from 'components/Sidebar/SidebarDivider';

const useStyles = makeStyles(() => ({
  menuItemNotFound: {
    fontSize: '14px',
    fontWeight: 400,
    color: '#adadad',
    textAlign: 'center',
  },
}));

interface ISidebarRoutes {
  routeItems: RouteItem[];
}

const SidebarRoutes: FC<ISidebarRoutes> = ({ routeItems }) => {
  const intl = useIntl();
  const location = useLocation();
  const classes = useStyles();
  const { search, sidebarOpen } = useSidebarContext();
  const userCorp = useRootSelector(selectCurrentUserCorporation);
  const hasPermissions = useHasPermissions();
  const visibleSidebarItems: RouteItem[] = [];
  const sidebarItems: RouteItem[] = [];

  const hasNavLinkPermissions = (permissions?: Permission[]): boolean => {
    return !permissions || hasPermissions(permissions);
  };

  const hasGroupTitleItems = (items: RouteItem[], relatesTo: string[]): boolean => {
    if (relatesTo) {
      return some(items, item => relatesTo.includes(item.id));
    }

    return true;
  };

  const hasActiveSubmenuNavLink = (container: IRouteItemContainer): boolean => {
    if (container.path === location.pathname) {
      return true;
    }

    return some(container.children, item => {
      if (item.type === 'navLink') {
        return location.pathname.startsWith(item.path);
      }

      return false;
    });
  };

  const isNavLinkVisible = (title: string): boolean => {
    return !search || title.toLowerCase().includes(search.toLowerCase());
  };

  for (const item of routeItems) {
    if (item.type === 'container') {
      const visibleChildren: RouteContainerChild[] = [];
      const children: RouteContainerChild[] = [];

      for (const child of item.children) {
        if (child.type === 'navLink') {
          if (hasNavLinkPermissions(child.permissions)) {
            const childTitle = intl.formatMessage(child.title);

            if (isNavLinkVisible(childTitle)) {
              visibleChildren.push(child);
            }
          }
        } else if (child.type === 'groupTitle') {
          visibleChildren.push(child);
        }
      }

      for (const child of visibleChildren) {
        if (child.type === 'navLink') {
          children.push(child);
        } else if (child.type === 'groupTitle') {
          if (hasGroupTitleItems(visibleChildren, child.relatesTo)) {
            children.push(child);
          }
        }
      }

      if (children.length) {
        visibleSidebarItems.push({ ...item, children });
      }
    } else if (item.type === 'navLink') {
      if (hasNavLinkPermissions(item.permissions)) {
        const title = intl.formatMessage(item.title);

        if (isNavLinkVisible(title)) {
          visibleSidebarItems.push(item);
        }
      }
    } else {
      visibleSidebarItems.push(item);
    }
  }

  for (const item of visibleSidebarItems) {
    if (item.type === 'groupTitle') {
      if (sidebarOpen) {
        if (hasGroupTitleItems(visibleSidebarItems, item.relatesTo)) {
          sidebarItems.push(item);
        }
      } else {
        sidebarItems.push({ type: 'divider', id: item.id });
      }
    } else {
      sidebarItems.push(item);
    }
  }

  if (sidebarItems[0] && sidebarItems[0].type === 'divider') {
    sidebarItems.shift();
  }

  if (sidebarItems.length && sidebarItems[sidebarItems.length - 1].type === 'divider') {
    sidebarItems.pop();
  }

  if (search && !sidebarItems.length) {
    return (
      <div className={classes.menuItemNotFound}>
        {intl.formatMessage(sidebarMessages.menuItemNotFound)}
      </div>
    );
  }

  return (
    <>
      {sidebarItems.map(item => {
        if (item.type === 'container') {
          return (
            <SidebarContainer
              key={item.id}
              id={item.id}
              title={item.title}
              path={item.path}
              icon={item.icon}
              iconViewBox={item.iconViewBox}
              active={hasActiveSubmenuNavLink(item)}
            >
              {displayNavLinkTitle => {
                return item.children.map(child => {
                  if (child.type === 'navLink') {
                    return (
                      <SideBarNavLink
                        key={child.id}
                        id={child.id}
                        to={child.path}
                        title={child.title}
                        displayTitle={displayNavLinkTitle}
                      />
                    );
                  } else if (child.type === 'groupTitle') {
                    if (child.groupTitle) {
                      return <SidebarGroupTitle key={child.id} groupTitle={child.groupTitle} />;
                    }
                  }

                  return null;
                });
              }}
            </SidebarContainer>
          );
        } else if (item.type === 'navLink') {
          return (
            <SideBarNavLink
              key={item.id}
              id={item.id}
              to={item.path}
              title={item.title}
              icon={item.icon}
              iconViewBox={item.iconViewBox}
              displayTooltip
            />
          );
        } else if (item.type === 'groupTitle') {
          const gTitle = item.id === 'corporationName' ? userCorp?.get('title') : item.groupTitle;

          if (gTitle) {
            return <SidebarGroupTitle key={item.id} groupTitle={gTitle} />;
          }
        } else if (item.type === 'divider') {
          return <SidebarDivider key={item.id} />;
        }

        return null;
      })}
    </>
  );
};

export default SidebarRoutes;
